/**
 * Pure, dependency-light HTML → readable plain text — shared by the dashboard.
 *
 * Some feed Sources hand us the posting body as raw HTML (RemoteOK and Working
 * Nomads return `description` as an HTML blob; their source modules store it
 * verbatim). The job card renders the description as text, so without this the
 * raw `<p>`/`<h1>`/`<li>`/`<strong style=…>` tags show literally in the UI.
 *
 * Stripping in the DISPLAY layer (rather than per-Source at ingest) fixes every
 * Source at once AND every row already persisted in D1 — a per-adapter cleanup
 * would only help rows ingested after the change. Rendering the feed HTML
 * directly (dangerouslySetInnerHTML) is deliberately avoided: third-party feed
 * markup is an XSS vector and the repo stays dependency-light (no sanitizer).
 *
 * Mirrors the runner-side `htmlToMultilineText` (runner/htmlExtract.js) so feed
 * descriptions read the same whether cleaned at ingest or at render time.
 */

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  mdash: '—', ndash: '–', hellip: '…', copy: '©', reg: '®', trade: '™',
};

/** Decode the handful of HTML entities listing/feed bodies actually emit. */
function decodeEntities(s) {
  return String(s)
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => {
      const code = parseInt(h, 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : '';
    })
    .replace(/&#(\d+);/g, (_, n) => {
      const code = Number(n);
      return Number.isFinite(code) ? String.fromCodePoint(code) : '';
    })
    .replace(/&(\w+);/g, (m, name) =>
      Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name] : m
    );
}

/** Does this string carry at least one HTML tag? (Cheap detector so plain-text
 *  descriptions — e.g. Upwork — pass through untouched and keep their spacing.) */
export function looksLikeHtml(s) {
  return typeof s === 'string' && /<\/?[a-z][a-z0-9]*\b[^>]*>/i.test(s);
}

/**
 * Convert an HTML description to readable multiline text: block/break tags
 * become line breaks, all other tags are removed, entities decoded, whitespace
 * collapsed per line and blank lines dropped. Plain (non-HTML) text is returned
 * essentially unchanged (only entity-decoded + trimmed). Never throws.
 */
export function htmlToText(input) {
  if (!input) return '';
  let s = String(input).replace(/\\n/g, '\n');
  if (!looksLikeHtml(s)) {
    // Plain text — leave structure intact, just trim trailing whitespace.
    return decodeEntities(s).replace(/[ \t]+$/gm, '').trim();
  }
  s = s
    .replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6]|ul|ol|tr|section|article|header|footer|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return decodeEntities(s)
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
    .trim();
}
